import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';

/** PageState 안의 이동 링크 하나 */
export interface PageStateAction {
    label: string;
    to: string;
}

export interface PageStateProps {
    /** 한 줄 제목 — 무슨 상태인지 (예: "불러오지 못했습니다") */
    title: string;
    /** 제목 아래 보조 설명 */
    description?: React.ReactNode;
    /** 주 동작 링크 */
    action?: PageStateAction;
    /** 보조 동작 링크 */
    secondaryAction?: PageStateAction;
    /** 지정하면 "다시 시도" 버튼을 노출한다 */
    onRetry?: () => void;
    /** 에러 상태 — 스크린리더에 즉시 알리고(role="alert") 제목을 경고 색으로 표시한다 */
    error?: boolean;
}

/** 상자·테두리 없이 흰 셸 안에 놓인다(관심치과 빈 상태 기준, 마이페이지 감사 2026-10-06 C 묶음) */
const Card = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin: 0 auto;
    padding: 48px 16px;
    max-width: 480px;
    box-sizing: border-box;
    text-align: center;
    word-break: keep-all;
    overflow-wrap: anywhere;

    @media (max-width: 768px) {
        padding: 40px 0;
    }
`;

/** 무슨 상태인지 한 줄 — 15px 흐림(#66736F), 실패는 오류 빨강 하나(#E11D48) */
const Title = styled('p', { shouldForwardProp: (prop: string) => prop !== 'error' })<{ error: boolean }>`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.6;
    color: ${({ error }) => (error ? FORM_COLOR.danger : FORM_COLOR.muted)};
`;

/** 보조 설명 — 13px 흐림 */
const Description = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: ${FORM_COLOR.muted};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 8px;
`;

/** 다음 행동 버튼 — 높이 44·반경 14·좌우 20 대칭(가이드 4·4.1). 주 버튼은 청록, 보조는 흰 바탕 테두리 */
const actionButton = (primary: boolean): string => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 44px;
    padding: 0 20px;
    border-radius: 14px;
    border: ${primary ? 'none' : `1px solid ${FORM_COLOR.border}`};
    background: ${primary ? FORM_COLOR.primary : '#fff'};
    color: ${primary ? '#fff' : FORM_COLOR.sub};
    font-family: inherit;
    font-size: 15px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        background: ${primary ? FORM_COLOR.primaryDeep : FORM_COLOR.lineSoft};
    }
    &:focus-visible {
        outline: 2px solid ${FORM_COLOR.primary};
        outline-offset: 2px;
    }
`;

const PrimaryLink = styled(Link)`${actionButton(true)}`;
const SecondaryLink = styled(Link)`${actionButton(false)}`;
const RetryButton = styled.button`${actionButton(true)}`;

/**
 * 목록·화면이 "내용 없음" 또는 "불러오기 실패" 일 때 쓰는 공용 안내(빈 상태 하나 — 문구 + 다음 행동 버튼).
 *
 * 실패를 빈 목록처럼 조용히 보여주면 사용자는 "글이 없구나" 로 오해하고 떠난다.
 * 그래서 실패는 [error] 로 구분해 role="alert" 로 알리고 [onRetry] 로 되돌릴 길을 준다.
 *
 * @example
 * <PageState title="불러오지 못했습니다" description="잠시 후 다시 시도해 주세요." error onRetry={reload} />
 * <PageState title="아직 작성한 글이 없어요" action={{ label: '글 쓰러 가기', to: '/posts/write' }} />
 */
const PageState: React.FC<PageStateProps> = ({ title, description, action, secondaryAction, onRetry, error = false }) => {
    const hasActions: boolean = action != null || secondaryAction != null || onRetry != null;

    return (
        <Card role={error ? 'alert' : 'status'}>
            <Title error={error}>{title}</Title>
            {description != null && <Description>{description}</Description>}
            {hasActions && (
                <Actions>
                    {onRetry != null && (
                        <RetryButton type="button" onClick={onRetry}>
                            다시 시도
                        </RetryButton>
                    )}
                    {action != null && (
                        onRetry != null
                            ? <SecondaryLink to={action.to}>{action.label}</SecondaryLink>
                            : <PrimaryLink to={action.to}>{action.label}</PrimaryLink>
                    )}
                    {secondaryAction != null && <SecondaryLink to={secondaryAction.to}>{secondaryAction.label}</SecondaryLink>}
                </Actions>
            )}
        </Card>
    );
};

export default PageState;
